// @ts-nocheck
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { useDropzone } from 'react-dropzone';
import { formatBytes } from '@/lib/utils';
import { Id } from '@/convex/_generated/dataModel';
import { cn } from '@/lib/utils';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useAuth } from '@/hooks/useAuth';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { motion, AnimatePresence } from 'framer-motion';
import { Img } from 'remotion';
import {
	Loader2,
	Sparkles,
	Paintbrush,
	Image as ImageIcon,
	RefreshCw,
	Trash2,
	UploadCloud,
	X,
} from 'lucide-react';
import { UploadIcon } from '@/components/ui/icons/upload-icon';
import { AudioLinesIcon } from '@/components/ui/icons/audio-lines-icon';
import { Input } from '@/components/ui/input';
import { AnalysisDialog } from '@/components/ui/analysis-dialog';
import { useConfetti } from '@/hooks/useConfetti';
import ReactMarkdown from 'react-markdown';
import { Textarea } from '@/components/ui/textarea';
import { PageLoader, Loader } from '@/components/ui/loader';

interface Document {
	_id: Id<'documents'>;
	_creationTime: number;
	name: string;
	fileId: string;
	size: number;
	type: string;
	uploadedBy: Id<'users'>;
	profileId: Id<'profiles'>;
	createdAt: string;
	updatedAt: string;
}

type UploadUrlResponse = {
	url: string;
	id: Id<'_storage'>;
};

// Brand Style interface
interface BrandStyle {
	title: string;
	styleAesthetic: {
		overallVibe: string;
		colorPalette: {
			background: string;
			accents: string[];
		};
		materialsAndTextures: {
			type: string;
			description: string;
		}[];
		lightingAndShadows: {
			description: string;
		};
		moodKeywords: string[];
	};
}

interface BrandStyleImage {
	_id: Id<'brandStyleImages'>;
	profileId: Id<'profiles'>;
	prompt: string;
	styleJson: string;
	name: string;
	downloadUrl: string;
	createdAt: string;
}

export function BrandVoice() {
	// 1. Context hooks
	const { user, isSuperAdmin } = useAuth();
	const { toast } = useToast();
	const { triggerConfetti, Confetti } = useConfetti();

	// 2. Query hooks
	const profiles = useQuery(api.profiles.list, {
		userId: user?.id as unknown as Id<'users'>,
		isSuperAdmin: user?.role === 'superadmin',
	});
	const documents = useQuery(api.documents.listDocuments, {});
	const documentUrls = useQuery(api.documents.getDocumentUrl, {});

	// 3. Mutation hooks
	const generateUploadUrl = useMutation(api.documents.generateUploadUrl);
	const storeDocument = useMutation(api.documents.storeDocument);
	const deleteDocument = useMutation(api.documents.deleteDocument);
	const clearWritingAnalysis = useMutation(
		api.profiles.updateWritingAnalysis
	);

	// 4. State hooks - Move ALL state hooks here
	const [searchTerm, setSearchTerm] = React.useState('');
	const [selectedProfileId, setSelectedProfileId] = React.useState<
		Id<'profiles'> | ''
	>('');
	const [lastUploadedProfileId, setLastUploadedProfileId] =
		React.useState<Id<'profiles'> | null>(null);
	const [uploadProgress, setUploadProgress] = React.useState<{
		[key: string]: number;
	}>({});
	const [uploadError, setUploadError] = React.useState<string | null>(null);
	const [error, setError] = React.useState<string | null>(null);
	const [documentToDelete, setDocumentToDelete] = React.useState<{
		id: Id<'documents'>;
		name: string;
	} | null>(null);
	const [expandedProfiles, setExpandedProfiles] = React.useState<Set<string>>(
		new Set()
	);
	const [isUploading, setIsUploading] = React.useState(false);
	const [isAnalyzing, setIsAnalyzing] = React.useState(false);
	const [analysisStatus, setAnalysisStatus] = React.useState<
		'uploading' | 'analyzing' | null
	>(null);
	const [editingProfileId, setEditingProfileId] =
		React.useState<Id<'profiles'> | null>(null);
	const [editedAnalysis, setEditedAnalysis] = React.useState<string>('');
	const [showSaveWarning, setShowSaveWarning] = React.useState(false);
	const [isIconHovered, setIsIconHovered] = React.useState(false);

	// New hooks for brand style
	const [brandStyle, setBrandStyle] = React.useState<BrandStyle | null>(null);
	const [stylePrompt, setStylePrompt] = React.useState('');
	const [isGeneratingImage, setIsGeneratingImage] = React.useState(false);
	const [selectedBrandStyleProfileId, setSelectedBrandStyleProfileId] =
		React.useState<Id<'profiles'> | ''>('');

	// New queries and mutations for brand style images
	const generateBrandStyleImage = useAction(
		api.brandStyleImages.generateBrandStyleImage as any
	);
	const brandStyleImages = useQuery(
		api.brandStyleImages.listBrandStyleImages,
		selectedBrandStyleProfileId
			? { profileId: selectedBrandStyleProfileId as Id<'profiles'> }
			: 'skip'
	);
	const deleteBrandStyleImage = useMutation(
		api.brandStyleImages.deleteBrandStyleImage
	);

	// New hooks for source image upload
	const [sourceImage, setSourceImage] = React.useState<{
		file: File | null;
		preview: string | null;
		storageId: Id<'_storage'> | null;
	}>({
		file: null,
		preview: null,
		storageId: null,
	});
	const [isUploadingSourceImage, setIsUploadingSourceImage] =
		React.useState(false);

	// New action for source image upload (changed from mutation to action)
	const uploadSourceImage = useAction(
		api.brandStyleImages.uploadSourceImage as any
	);

	// 5. Derived state
	const isDocumentsLoading = !documents || !documentUrls;
	const userEmail = user?.email?.toLowerCase() || '';

	// 6. Effects
	React.useEffect(() => {
		// console.log('Effect running with:', {
		// 	isAnalyzing,
		// 	lastUploadedProfileId,
		// 	status: analysisStatus,
		// 	profileFound: lastUploadedProfileId
		// 		? profiles?.find(
		// 				(p: { _id: Id<'profiles'> }) =>
		// 					p._id === lastUploadedProfileId
		// 			)
		// 		: null,
		// });

		if (isAnalyzing && lastUploadedProfileId) {
			const profile = profiles?.find(
				(p: { _id: Id<'profiles'>; writingAnalysis?: string }) =>
					p._id === lastUploadedProfileId
			);
			console.log('Found profile:', profile);

			if (
				profile?.writingAnalysis &&
				profile.writingAnalysis.trim() !== ''
			) {
				console.log('Analysis complete, cleaning up states');
				setIsAnalyzing(false);
				setAnalysisStatus(null);
				setLastUploadedProfileId(null);
				setSelectedProfileId('');
				triggerConfetti();
			}
		}
	}, [
		isAnalyzing,
		lastUploadedProfileId,
		profiles,
		analysisStatus,
		triggerConfetti,
		setSelectedProfileId,
	]);

	// Initialize brand style on component mount
	React.useEffect(() => {
		if (!brandStyle) {
			setBrandStyle({
				title: 'Astral Candy Coating',
				styleAesthetic: {
					overallVibe: 'Cosmic glossy surrealism',
					colorPalette: {
						background: 'Gradient galaxy',
						accents: [
							'Metallic pinks',
							'Glazed purples',
							'Sour apple green',
						],
					},
					materialsAndTextures: [
						{
							type: 'Glossy sugar coat',
							description:
								'Hard candy shell with light refraction and sugar sparkle',
						},
						{
							type: 'Soft nebula inner texture',
							description:
								'Swirled cloud-like gradients under the surface',
						},
					],
					lightingAndShadows: {
						description:
							'Celestial rim lights with aurora highlights',
					},
					moodKeywords: [
						'Playful',
						'Extraterrestrial',
						'Slick',
						'Whimsical',
					],
				},
			});
		}
	}, [brandStyle]);

	// 7. Callbacks
	const handleDeleteDocument = React.useCallback(
		async (documentId: Id<'documents'>) => {
			try {
				const doc = documents?.find(
					(d: Document) => d._id === documentId
				);
				if (doc?.profileId) {
					await clearWritingAnalysis({
						profileId: doc.profileId,
						analysis: '',
					});
				}
				await deleteDocument({ documentId });
				toast({
					title: '✨ Document Deleted',
					description: 'Document successfully deleted',
					duration: 3000,
				});
			} catch (error) {
				toast({
					title: 'Error',
					description:
						error instanceof Error
							? error.message
							: 'Failed to delete document',
					variant: 'destructive',
					duration: 3000,
				});
			}
		},
		[documents, clearWritingAnalysis, deleteDocument, toast]
	);

	const handleStartEdit = React.useCallback(
		(profileId: Id<'profiles'>, currentAnalysis: string) => {
			setEditingProfileId(profileId);
			setEditedAnalysis(currentAnalysis);
		},
		[setEditingProfileId, setEditedAnalysis]
	);

	const handleCancelEdit = React.useCallback(() => {
		setEditingProfileId(null);
		setEditedAnalysis('');
	}, [setEditingProfileId, setEditedAnalysis]);

	const handleSave = React.useCallback(
		async (profileId: Id<'profiles'>) => {
			try {
				await clearWritingAnalysis({
					profileId,
					analysis: editedAnalysis,
				});
				setEditingProfileId(null);
				setEditedAnalysis('');
				toast({
					title: '✨ Analysis Updated',
					description:
						'Brand voice analysis has been updated successfully',
					duration: 3000,
				});
			} catch (error) {
				toast({
					title: 'Error',
					description:
						error instanceof Error
							? error.message
							: 'Failed to update analysis',
					variant: 'destructive',
					duration: 3000,
				});
			}
		},
		[clearWritingAnalysis, editedAnalysis, toast]
	);

	const onDrop = React.useCallback(
		async (acceptedFiles: File[]) => {
			try {
				if (!selectedProfileId) {
					toast({
						title: 'Profile Required',
						description:
							'Please select a social profile before uploading documents.',
						variant: 'destructive',
						duration: 3000,
					});
					return;
				}

				// Check file size (max 1MB)
				const file = acceptedFiles[0];
				if (file.size > 1024 * 1024) {
					toast({
						title: 'File Too Large',
						description: 'Please upload a file smaller than 1MB',
						variant: 'destructive',
						duration: 3000,
					});
					return;
				}

				// Check word count
				const text = await file.text();
				const wordCount = text.trim().split(/\s+/).length;
				if (wordCount > 15000) {
					toast({
						title: 'Document Too Large',
						description:
							'Please limit your document to 15,000 words or less',
						variant: 'destructive',
						duration: 3000,
					});
					return;
				}

				setIsAnalyzing(true);
				setAnalysisStatus('uploading');
				setLastUploadedProfileId(selectedProfileId);

				// 1. Generate upload URL
				const uploadUrl = await generateUploadUrl();

				// 2. Upload file and track progress
				const xhr = new XMLHttpRequest();

				xhr.upload.onprogress = (event) => {
					if (event.lengthComputable) {
						const progress = Math.round(
							(event.loaded * 100) / event.total
						);
						setUploadProgress((prev) => ({
							...prev,
							[file.name]: progress,
						}));
					}
				};

				const { storageId } = await new Promise<{
					storageId: string;
				}>((resolve, reject) => {
					xhr.onload = async () => {
						try {
							const response = JSON.parse(xhr.responseText);
							resolve(response);
						} catch (error) {
							reject(
								new Error('Failed to parse upload response')
							);
						}
					};
					xhr.onerror = () => reject(new Error('Upload failed'));
					xhr.open('POST', uploadUrl);
					xhr.send(file);
				});

				// Clear the upload progress for this file
				setUploadProgress((prev) => {
					const next = { ...prev };
					delete next[file.name];
					return next;
				});

				// After successful upload, change status to analyzing
				setAnalysisStatus('analyzing');

				// 3. Store document in database
				await storeDocument({
					name: file.name,
					fileId: storageId as Id<'_storage'>,
					size: file.size,
					type: file.type,
					profileId: selectedProfileId,
				});

				toast({
					title: '✨ Upload Complete',
					description: `Successfully uploaded ${file.name}`,
					duration: 3000,
				});
			} catch (error) {
				const errorMessage =
					error instanceof Error
						? error.message
						: 'Failed to upload file';
				setUploadError(errorMessage);
				setIsAnalyzing(false);
				toast({
					title: 'Upload Error',
					description: errorMessage,
					variant: 'destructive',
					duration: 3000,
				});
				setTimeout(() => setUploadError(null), 3000);
			}
		},
		[
			generateUploadUrl,
			selectedProfileId,
			storeDocument,
			toast,
			setUploadError,
			setIsAnalyzing,
			setAnalysisStatus,
			setLastUploadedProfileId,
			setUploadProgress,
		]
	);

	const onDropRejected = React.useCallback(
		(rejectedFiles: any[]) => {
			if (!selectedProfileId) {
				toast({
					title: 'Profile Required',
					description:
						'Please select a social profile before uploading documents.',
					variant: 'destructive',
					duration: 3000,
				});
				return;
			}

			toast({
				title: 'Invalid File Type',
				description: 'Only .txt files are supported at this time',
				variant: 'destructive',
				duration: 3000,
			});
		},
		[toast, selectedProfileId]
	);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		onDropRejected,
		noClick: !selectedProfileId,
		accept: {
			'text/plain': ['.txt'],
		},
		maxFiles: 1,
		validator: (file) => {
			if (!selectedProfileId) {
				return {
					code: 'profile-required',
					message: 'Please select a profile',
				};
			}
			if (file.type !== 'text/plain') {
				return {
					code: 'invalid-file-type',
					message: 'Only .txt files are supported',
				};
			}
			return null;
		},
	});

	// 8. Memoized values
	const availableProfiles = React.useMemo(() => {
		if (!profiles || !userEmail) return [];
		if (isSuperAdmin) return profiles;
		return profiles.filter(
			(profile: any) =>
				(profile.userId as any) === (user?.id as any) ||
				profile.email.toLowerCase() === userEmail
		);
	}, [profiles, userEmail, user?.id, isSuperAdmin]);

	const filteredDocuments = React.useMemo(() => {
		if (!documents || !userEmail) return [];
		if (isSuperAdmin) return documents;
		return documents.filter((doc: Document) => {
			const profile = profiles?.find((p: any) => p._id === doc.profileId);
			return (
				profile &&
				((profile.userId as any) === (user?.id as any) ||
					profile.email.toLowerCase() === userEmail)
			);
		});
	}, [documents, profiles, userEmail, user?.id, isSuperAdmin]);

	const groupedDocuments = React.useMemo(() => {
		if (!filteredDocuments) return {};
		return filteredDocuments.reduce(
			(acc: Record<string, typeof documents>, doc: Document) => {
				const profileId = doc.profileId;
				if (!acc[profileId]) acc[profileId] = [];
				acc[profileId].push(doc);
				return acc;
			},
			{}
		);
	}, [filteredDocuments]);

	const sortedGroupedDocuments = React.useMemo(() => {
		if (!groupedDocuments || !availableProfiles || !userEmail) return [];
		return Object.entries(groupedDocuments).sort(
			([profileIdA], [profileIdB]) => {
				const profileA = availableProfiles.find(
					(p: { _id: Id<'profiles'> }) => p._id === profileIdA
				);
				const profileB = availableProfiles.find(
					(p: { _id: Id<'profiles'> }) => p._id === profileIdB
				);
				if (!profileA || !profileB) return 0;
				if (profileA.writingAnalysis && profileB.writingAnalysis) {
					return (profileB.lastUpdated || '').localeCompare(
						profileA.lastUpdated || ''
					);
				}
				if (profileA.writingAnalysis) return -1;
				if (profileB.writingAnalysis) return 1;
				const isProfileAUsers =
					profileA.email.toLowerCase() === userEmail;
				const isProfileBUsers =
					profileB.email.toLowerCase() === userEmail;
				if (isProfileAUsers && !isProfileBUsers) return -1;
				if (!isProfileAUsers && isProfileBUsers) return 1;
				return (profileA.profileTitle || '').localeCompare(
					profileB.profileTitle || ''
				);
			}
		);
	}, [groupedDocuments, availableProfiles, userEmail]);

	// Dropzone for source image upload
	const {
		getRootProps: getSourceImageRootProps,
		getInputProps: getSourceImageInputProps,
		isDragActive: isSourceImageDragActive,
		open: openSourceImageFileDialog,
	} = useDropzone({
		onDrop: async (acceptedFiles) => {
			if (acceptedFiles.length > 0) {
				const file = acceptedFiles[0];

				// Create preview
				const reader = new FileReader();
				reader.onload = () => {
					setSourceImage({
						...sourceImage,
						file,
						preview: reader.result as string,
					});
				};
				reader.readAsDataURL(file);

				// Upload the image
				try {
					setIsUploadingSourceImage(true);

					// Convert file to data URL with base64
					const dataUrl = await new Promise<string>((resolve) => {
						const reader = new FileReader();
						reader.onloadend = () => {
							// Use the complete data URL
							resolve(reader.result as string);
						};
						reader.readAsDataURL(file);
					});

					// Upload to storage with the full data URL
					const { sourceImageId } = await uploadSourceImage({
						profileId:
							selectedBrandStyleProfileId as Id<'profiles'>,
						dataUrl: dataUrl,
					});

					// Update state with storage ID
					setSourceImage({
						file,
						preview: URL.createObjectURL(file),
						storageId: sourceImageId,
					});

					toast({
						title: '✨ Image Uploaded',
						description: 'Source image uploaded successfully',
						duration: 3000,
					});
				} catch (error) {
					toast({
						title: 'Upload Error',
						description:
							error instanceof Error
								? error.message
								: 'Failed to upload image',
						variant: 'destructive',
						duration: 3000,
					});
				} finally {
					setIsUploadingSourceImage(false);
				}
			}
		},
		accept: {
			'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
		},
		multiple: false,
		noClick: true,
	});

	// Clear source image
	const clearSourceImage = () => {
		if (sourceImage.preview) {
			URL.revokeObjectURL(sourceImage.preview);
		}
		setSourceImage({
			file: null,
			preview: null,
			storageId: null,
		});
	};

	// Updated generate image handler to include source image if available
	const handleGenerateImage = React.useCallback(async () => {
		if (!selectedBrandStyleProfileId || !stylePrompt || !brandStyle) return;

		setIsGeneratingImage(true);
		try {
			const result = await generateBrandStyleImage({
				profileId: selectedBrandStyleProfileId as Id<'profiles'>,
				prompt: stylePrompt,
				styleJson: JSON.stringify(brandStyle),
				size: '1024x1024',
				quality: 'high',
				sourceImageId: sourceImage.storageId || undefined,
			});

			console.log('Image generation result:', result);

			toast({
				title: '✨ Image Generated',
				description: sourceImage.storageId
					? 'Image retextured successfully'
					: 'Brand style image successfully generated',
				duration: 3000,
			});

			setStylePrompt('');

			// Refresh brand images by forcing a delay to allow database to update
			setTimeout(() => {
				// This is a bit of a hack but works for Convex by causing a re-render
				setSelectedBrandStyleProfileId((prev) => {
					// Toggle and reset to force a refetch
					if (prev) {
						const current = prev;
						setSelectedBrandStyleProfileId('');
						setTimeout(
							() => setSelectedBrandStyleProfileId(current),
							100
						);
					}
					return prev;
				});
			}, 1000);
		} catch (error) {
			toast({
				title: 'Error Generating Image',
				description:
					error instanceof Error
						? error.message
						: 'Failed to generate image',
				variant: 'destructive',
				duration: 3000,
			});
		} finally {
			setIsGeneratingImage(false);
		}
	}, [
		selectedBrandStyleProfileId,
		stylePrompt,
		brandStyle,
		generateBrandStyleImage,
		toast,
		sourceImage.storageId,
	]);

	// Handle deleting a brand style image
	const handleDeleteBrandStyleImage = React.useCallback(
		async (imageId: Id<'brandStyleImages'>) => {
			try {
				await deleteBrandStyleImage({ imageId });
				toast({
					title: '✨ Image Deleted',
					description: 'Brand style image successfully deleted',
					duration: 3000,
				});

				// Refresh the list after deletion
				setTimeout(() => {
					// This is a bit of a hack but works for Convex by causing a re-render
					setSelectedBrandStyleProfileId((prev) => {
						if (prev) {
							const current = prev;
							setSelectedBrandStyleProfileId('');
							setTimeout(
								() => setSelectedBrandStyleProfileId(current),
								100
							);
						}
						return prev;
					});
				}, 500);
			} catch (error) {
				toast({
					title: 'Error',
					description:
						error instanceof Error
							? error.message
							: 'Failed to delete image',
					variant: 'destructive',
					duration: 3000,
				});
			}
		},
		[deleteBrandStyleImage, toast]
	);

	// 9. Loading check - after all hooks
	if (!profiles) {
		return <PageLoader message='LOADING BRAND VOICE...' />;
	}

	return (
		<>
			<div className='container mx-auto p-6 pb-80'>
				<div className='flex justify-between items-center mb-6'>
					<div>
						<h1 className='text-3xl font-bold py-4'>
							Brand Voice Documents
						</h1>
						<p className='text-lg text-gray-500 -mt-2 mb-4'>
							{isSuperAdmin
								? 'Upload and manage brand voice documents for all social profiles'
								: 'Upload and manage brand voice documents for your social profiles'}
						</p>
					</div>
				</div>

				<Card className='glow-card'>
					<CardHeader>
						<CardTitle className='pink-title'>
							<span style={{ color: '#000' }}></span> Upload
							Documents
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className='space-y-4 mb-4'>
							<SearchableSelect
								value={selectedProfileId}
								onValueChange={setSelectedProfileId}
								items={availableProfiles}
								searchTerm={searchTerm}
								setSearchTerm={setSearchTerm}
								placeholder='Select a profile'
								valueField='_id'
								renderItem={(profile) => (
									<div className='flex flex-col'>
										<span className='font-medium'>
											{(profile as any).profileTitle}
										</span>
										<span className='text-sm text-muted-foreground'>
											{(profile as any).email}
										</span>
									</div>
								)}
							/>

							{!selectedProfileId ? (
								<Alert>
									<AlertTitle>Select a Profile</AlertTitle>
									<AlertDescription>
										Please select a social profile above to
										upload documents
									</AlertDescription>
								</Alert>
							) : documents?.some(
									(doc: Document) =>
										doc.profileId === selectedProfileId
							  ) ? (
								<Alert>
									<AlertTitle>
										Document Already Uploaded
									</AlertTitle>
									<AlertDescription>
										Please delete the existing document
										before uploading a new one
									</AlertDescription>
								</Alert>
							) : (
								<div
									{...getRootProps()}
									className={cn(
										'border-2 border-dashed rounded-lg p-8 text-center transition-colors',
										isDragActive
											? 'border-pink-500 bg-pink-50 dark:bg-pink-900/20'
											: 'border-gray-300 dark:border-gray-700 hover:border-pink-500 cursor-pointer'
									)}
								>
									<input {...getInputProps()} />
									<div className='space-y-4 cursor-inherit'>
										<UploadIcon />
										<div className='space-y-2 cursor-inherit'>
											<p className='text-sm font-medium'>
												{isDragActive
													? 'Drop the files here...'
													: 'Drag & drop files here, or click to select'}
											</p>
											<p className='text-xs text-gray-500'>
												Only .txt files are supported at
												this time
											</p>
										</div>
									</div>
								</div>
							)}

							{/* Upload Progress */}
							{Object.entries(uploadProgress).map(
								([fileName, progress]) => (
									<div key={fileName} className='space-y-2'>
										<div className='flex justify-between text-sm'>
											<span>{fileName}</span>
											<span>{Math.round(progress)}%</span>
										</div>
										<div className='h-8 bg-gray-200 rounded-xl overflow-hidden'>
											<div
												className='h-full bg-gradient-to-b from-purple-400 to-purple-800 transition-all duration-300'
												style={{
													width: `${progress}%`,
												}}
											/>
										</div>
									</div>
								)
							)}
						</div>
					</CardContent>
				</Card>

				{/* Moved error display here and removed glow */}
				{(uploadError || error) && (
					<Card className='mt-6'>
						<CardHeader>
							<CardTitle className='red-title'>
								<span style={{ color: '#000' }}>🔥</span> Error
							</CardTitle>
						</CardHeader>
						<CardContent>
							<Alert variant='destructive'>
								<AlertTitle>Error</AlertTitle>
								<AlertDescription>
									{uploadError || error}
								</AlertDescription>
							</Alert>
						</CardContent>
					</Card>
				)}

				{/* Separate card for uploaded documents */}
				{documents && (
					<Card className='mt-12'>
						<CardHeader>
							<div className='flex justify-between items-center'>
								<CardTitle className='text-2xl font-semibold text-orange-500'>
									Uploaded Documents
								</CardTitle>
								<div className='w-72'>
									<Input
										placeholder='Search profiles...'
										value={searchTerm}
										onChange={(e) =>
											setSearchTerm(e.target.value)
										}
										className='w-full'
									/>
								</div>
							</div>
						</CardHeader>
						<CardContent>
							{isDocumentsLoading ? (
								<div className='flex justify-center items-center min-h-[200px]'>
									<Loader message='LOADING DOCUMENTS...' />
								</div>
							) : (
								<div className='space-y-8'>
									{sortedGroupedDocuments
										.filter(([profileId]) => {
											const profile = profiles?.find(
												(p: { _id: Id<'profiles'> }) =>
													p._id === profileId
											);
											if (!profile) return false;

											const searchLower =
												searchTerm.toLowerCase();
											return (
												(profile as any).profileTitle
													.toLowerCase()
													.includes(searchLower) ||
												(profile as any).email
													.toLowerCase()
													.includes(searchLower)
											);
										})
										.map(([profileId, profileDocs]) => {
											const profile = profiles?.find(
												(p: { _id: Id<'profiles'> }) =>
													p._id === profileId
											);
											if (!profile) return null;

											const isUsersProfile =
												(
													profile as any
												).email.toLowerCase() ===
												user?.email?.toLowerCase();

											return (
												<div
													key={profileId}
													className={cn(
														'border rounded-lg p-4',
														isUsersProfile
															? 'border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-900/10'
															: 'border-gray-200 dark:border-gray-800'
													)}
												>
													<div className='space-y-2'>
														<div className='flex items-center gap-4'>
															<motion.button
																onClick={() =>
																	setExpandedProfiles(
																		(
																			prev
																		) => {
																			const next =
																				new Set(
																					prev
																				);
																			if (
																				next.has(
																					profileId
																				)
																			) {
																				next.delete(
																					profileId
																				);
																			} else {
																				next.add(
																					profileId
																				);
																			}
																			return next;
																		}
																	)
																}
																className='w-8 h-8 rounded-full bg-purple-900 flex items-center justify-center text-white hover:bg-purple-700 transition-colors'
																animate={{
																	rotate: expandedProfiles.has(
																		profileId
																	)
																		? 180
																		: 0,
																}}
																transition={{
																	duration: 0.2,
																}}
															>
																<span className='text-lg'>
																	{expandedProfiles.has(
																		profileId
																	)
																		? '🔽'
																		: '▶️'}
																</span>
															</motion.button>
															<div className='flex items-center gap-2 flex-1'>
																<h3 className='text-2xl font-bold text-purple-500'>
																	{
																		(
																			profile as any
																		)
																			.profileTitle
																	}
																</h3>
																{/* Add loading indicator */}
																{isUploading && (
																	<Loader2 className='h-4 w-4 animate-spin text-purple-500' />
																)}
															</div>
														</div>

														<div className='flex items-center gap-2'>
															<span
																className={cn(
																	'px-2 py-1 rounded-full text-xs font-semibold inline-flex items-center justify-center',
																	(
																		profile as any
																	).linkStatuses?.some(
																		(status: {
																			status: string;
																		}) =>
																			status.status ===
																			'linked'
																	)
																		? '!bg-orange-300 !text-orange-700'
																		: '!bg-gray-400 !text-gray-600'
																)}
															>
																{(
																	profile as any
																).linkStatuses?.some(
																	(status: {
																		status: string;
																	}) =>
																		status.status ===
																		'linked'
																)
																	? 'LINKED'
																	: 'NOT LINKED'}
															</span>
															<span
																className={cn(
																	'px-2 py-1 rounded-full text-xs font-semibold inline-flex items-center justify-center',
																	(
																		profile as any
																	)
																		.writingAnalysis
																		? '!bg-purple-300 !text-purple-700'
																		: '!bg-gray-400 !text-gray-600'
																)}
															>
																{(
																	profile as any
																)
																	.writingAnalysis
																	? 'ANALYZED'
																	: 'NOT ANALYZED'}
															</span>
															<span className='text-sm text-gray-500'>
																{
																	(
																		profile as any
																	).email
																}
															</span>
														</div>
														<p className='text-sm text-gray-500'>
															Documents:{' '}
															{Array.isArray(
																profileDocs
															)
																? profileDocs.length
																: 0}
														</p>
													</div>

													<AnimatePresence>
														{expandedProfiles.has(
															profileId
														) && (
															<motion.div
																className='space-y-4 mt-4'
																initial={{
																	opacity: 0,
																	height: 0,
																}}
																animate={{
																	opacity: 1,
																	height: 'auto',
																}}
																exit={{
																	opacity: 0,
																	height: 0,
																}}
																transition={{
																	duration: 0.2,
																}}
															>
																{/* Brand Voice Writing Analysis Section - Now inside expandable area */}
																{(
																	profile as any
																)
																	.writingAnalysis && (
																	<div
																		className='p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-purple-200 dark:border-purple-800'
																		onMouseEnter={() =>
																			setIsIconHovered(
																				true
																			)
																		}
																		onMouseLeave={() =>
																			setIsIconHovered(
																				false
																			)
																		}
																	>
																		<div className='flex justify-between items-center mb-2'>
																			<div className='flex items-center gap-2'>
																				<AudioLinesIcon
																					isHovered={
																						isIconHovered ||
																						editingProfileId ===
																							(
																								profile as any
																							)
																								._id
																					}
																				/>
																				<h2
																					className={cn(
																						'text-xl font-semibold transition-colors duration-200',
																						isIconHovered ||
																							editingProfileId ===
																								(
																									profile as any
																								)
																									._id
																							? 'text-[#FF69B4]'
																							: 'text-purple-600 dark:text-purple-400'
																					)}
																				>
																					Brand
																					Voice
																				</h2>
																			</div>
																			<div className='space-x-2'>
																				{editingProfileId ===
																				(
																					profile as any
																				)
																					._id ? (
																					<>
																						<Button
																							variant='outline'
																							size='sm'
																							onClick={
																								handleCancelEdit
																							}
																						>
																							Cancel
																						</Button>
																						<Button
																							variant='default'
																							size='sm'
																							onClick={() =>
																								setShowSaveWarning(
																									true
																								)
																							}
																						>
																							Save
																						</Button>
																					</>
																				) : (
																					<Button
																						variant='outline'
																						size='sm'
																						onClick={() =>
																							handleStartEdit(
																								(
																									profile as any
																								)
																									._id,
																								(
																									profile as any
																								)
																									.writingAnalysis ||
																									''
																							)
																						}
																					>
																						Edit
																					</Button>
																				)}
																			</div>
																		</div>
																		<div className='prose dark:prose-invert prose-sm max-w-none'>
																			{editingProfileId ===
																			(
																				profile as any
																			)
																				._id ? (
																				<Textarea
																					value={
																						editedAnalysis
																					}
																					onChange={(
																						e
																					) =>
																						setEditedAnalysis(
																							e
																								.target
																								.value
																						)
																					}
																					className='min-h-[200px] font-mono'
																					placeholder='Enter markdown content...'
																				/>
																			) : (
																				<ReactMarkdown>
																					{
																						(
																							profile as any
																						)
																							.writingAnalysis
																					}
																				</ReactMarkdown>
																			)}
																		</div>
																	</div>
																)}

																{/* Document Cards */}
																{Array.isArray(
																	profileDocs
																) &&
																	profileDocs.map(
																		(
																			doc: Document,
																			idx: number
																		) => (
																			<motion.div
																				key={
																					doc._id
																				}
																				initial={{
																					opacity: 0,
																					y: -10,
																				}}
																				animate={{
																					opacity: 1,
																					y: 0,
																					transition:
																						{
																							duration: 0.2,
																							delay:
																								idx *
																								0.05,
																						},
																				}}
																			>
																				<Card className='p-4'>
																					<div className='flex justify-between items-center'>
																						<div className='space-y-1'>
																							<p className='font-medium'>
																								{
																									doc.name
																								}
																							</p>
																							<span className='text-xs text-gray-400'>
																								{new Date(
																									doc.createdAt
																								).toLocaleDateString()}
																							</span>
																							<p className='text-sm text-gray-500'>
																								{formatBytes(
																									doc.size
																								)}{' '}
																								•{' '}
																								{
																									doc.type
																								}
																							</p>
																							<a
																								href={
																									documentUrls?.[
																										doc
																											._id
																									] ||
																									'#'
																								}
																								target='_blank'
																								rel='noopener noreferrer'
																								className='text-sm text-purple-500 hover:text-purple-700 underline truncate block max-w-[300px]'
																							>
																								View
																								Document
																							</a>
																						</div>
																						<Button
																							variant='destructive'
																							size='sm'
																							onClick={() =>
																								setDocumentToDelete(
																									{
																										id: doc._id,
																										name: doc.name,
																									}
																								)
																							}
																						>
																							🗑️
																							Delete
																						</Button>
																					</div>
																				</Card>
																			</motion.div>
																		)
																	)}
															</motion.div>
														)}
													</AnimatePresence>
												</div>
											);
										})}
								</div>
							)}
						</CardContent>
					</Card>
				)}

				<ConfirmDialog
					isOpen={documentToDelete !== null}
					onClose={() => setDocumentToDelete(null)}
					title='Delete Document'
					description={`Are you sure you want to delete "${documentToDelete?.name}"? This action cannot be undone.`}
					onConfirm={async () => {
						if (documentToDelete) {
							await handleDeleteDocument(documentToDelete.id);
							setDocumentToDelete(null);
						}
					}}
					isDeleteDialog={true}
				/>

				<AnalysisDialog
					isOpen={isAnalyzing}
					onOpenChange={setIsAnalyzing}
					status={analysisStatus}
				/>

				<Confetti />

				<ConfirmDialog
					isOpen={showSaveWarning}
					onClose={() => setShowSaveWarning(false)}
					title='Save Changes'
					description='Are you sure you want to save these changes? This will overwrite the existing brand voice analysis.'
					onConfirm={async () => {
						if (editingProfileId) {
							await handleSave(editingProfileId);
						}
						setShowSaveWarning(false);
					}}
				/>
			</div>
		</>
	);
}
